import { ref } from 'vue'
import { defineStore } from 'pinia'
import { searchPatient } from '@/services/api/fhirAPI/patient/patient.ts'
import { useSelectedPlanStore } from '@/stores/plans/selectedPlan.ts'
import { useCoverageStore } from '@/stores/coverage/coverage.ts'

export const usePatientStore = defineStore(
	'patientData',
	() => {
		// ====---------------====
		// State
		// ====---------------====
		const planStore = useSelectedPlanStore().getStore()
		const state = ref<IPatientData>({})
		const loaded = ref(false)

		// ====---------------====
		// Getters
		// ====---------------====
		async function loadStore(): Promise<IPatientData | null> {
			if (!loaded.value) await loadPatientData()

			return state.value
		}

		function getMemberName(memberId: string): TFhirName | null {
			return state.value[memberId].name?.[0] ?? null
		}

		// ====---------------====
		// Actions
		// ====---------------====
		function updatePatientData(memberId: string, payload: TFhirResource) {
			state.value[memberId] = payload
			loaded.value = true
		}

		function $reset() {
			state.value = {}
			loaded.value = false
		}

		function resetPatientData() {
			$reset()
		}

		// fetch patient data
		async function fetchPatient() {
			const requestId = planStore.isPolicyHolder ? planStore.contractId : planStore.memberId
			await searchPatient(requestId)
				.then((res: IPatientResponse) => {
					if (res.entry.length === 1 && res.entry[0].resource?.issue?.code == 'exception')
						throw new Error()
					res.entry.forEach((member: { [key: string]: any }) => {
						updatePatientData(member.resource.id, member.resource)
					})
				})
				.catch((err: string) => {
					console.error(err)
				})
		}

		// this isn't needed for the patient store, but there are some cases where both are needed (spending details for one)
		async function fetchCoverage() {
			if (!useCoverageStore().loaded) await useCoverageStore().loadCoverageStore()
		}

		let loadPatientRequest: Promise<string> | null = null
		async function loadPatientData() {
			if (loadPatientRequest) {
				return loadPatientRequest
			}

			loadPatientRequest = new Promise((resolve, reject) => {
				Promise.all([fetchPatient(), fetchCoverage()])
					.then(() => {
						resolve('')
					})
					.catch((err) => {
						reject(err)
					})
					.finally(() => {
						loadPatientRequest = null
					})
			})
			return loadPatientRequest
		}

		// ====---------------====
		// Return
		// ====---------------====
		return {
			state,
			loaded,
			getMemberName,
			loadPatientData,
			loadStore,
			updatePatientData,
			resetPatientData,
			$reset
		}
	},
	{
		persist: {
			storage: sessionStorage
		}
	}
)
